import React, { useEffect, useState } from "react";
import StoreLogo from "@/components/storefront/StoreLogo";
import ToneImg from "@/components/storefront/ToneImg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, Heart, Truck, X, ChevronUp, ChevronRight, MapPin, Mail, Star, Leaf, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/pharmacy/hooks";
import MobileNav from "@/components/storefront/MobileNav";
import { socialItems } from "@/lib/social";
import { inr, rating, reviews } from "@/lib/storeutils";
import { discountPct } from "@/lib/homesets";
import { navLinks, footerColumns, footerLines, trustItems, isNavActive } from "@/lib/storenav";

// Look-and-feel tokens of the "Fresh Market" layout: bright white, fresh green primary, orange accent, red price badges.
export const K = {
  primary: "var(--k-primary, #1b8a5a)", accent: "var(--k-accent, #ef7d1a)", ink: "#1c2a24", muted: "#6b7a72",
  line: "#e6ece8", soft: "#eef7f1", red: "#e5483b", script: "'Kaushan Script', 'Brush Script MT', cursive",
};
export const needsOptions = (p) => p.variants?.length > 0 || p.variant_items?.length > 0;

/** "₹30 – ₹38" for products whose variants differ in price, otherwise the plain price. */
export function priceText(p) {
  const prices = (p.variant_items || []).map((v) => v.price).filter((n) => n > 0);
  if (prices.length > 1) {
    const lo = Math.min(...prices), hi = Math.max(...prices);
    if (lo !== hi) return `${inr(lo)} – ${inr(hi)}`;
  }
  return inr(prices.length ? prices[0] : p.price);
}

/** The first option's values (sizes, packs) shown as small pills on the card. */
export function optionPills(p) {
  const vals = p.option_types?.[0]?.values || p.variants?.[0]?.options || [];
  return vals.slice(0, 3);
}

export function Stars({ id, size = 11, count = true, className = "" }) {
  const r = Math.round(rating(id));
  return (
    <span className={`inline-flex items-center gap-1 ${className}`} aria-label={`Rated ${rating(id).toFixed(1)} out of 5`}>
      <span className="inline-flex gap-px">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={size} fill={n <= r ? "#f5a524" : "none"} color={n <= r ? "#f5a524" : "#cbd5d0"} />)}</span>
      {count && <span className="text-[10px]" style={{ color: K.muted }}>({reviews(id)})</span>}
    </span>
  );
}

export function Discount({ p, className = "" }) {
  const off = discountPct(p);
  return off > 0 ? <span className={`mk-badge inline-flex items-center rounded px-1.5 py-[1px] text-[10px] font-bold text-white ${className}`} style={{ background: K.red }}>-{off}%</span> : null;
}

/** Section title, bold, with optional right-hand controls. */
export function MTitle({ children, right, line = false, className = "" }) {
  return (
    <div className={`flex items-center justify-between gap-3 flex-wrap ${className}`}>
      <div className="flex items-center gap-3 min-w-0">
        <h2 className="text-[20px] md:text-[24px] font-bold leading-tight" style={{ color: K.ink }}>{children}</h2>
        {line && <span className="mk-line h-[2px] w-12 md:w-16 rounded" style={{ background: K.primary }} />}
      </div>
      {right}
    </div>
  );
}

export function SeeMore({ to }) {
  return <Link to={to} className="mk-seemore inline-flex items-center gap-1 h-8 px-3 rounded-md border text-[11px] font-semibold transition-colors" style={{ borderColor: K.line, color: K.ink }}>See More <ChevronRight size={12} /></Link>;
}

function useAdd(p, s, cart) {
  const hasOptions = needsOptions(p);
  const [added, setAdded] = useState(false);
  const add = (e) => {
    e.preventDefault();
    if (hasOptions) { window.location.href = `/store/${s.slug}/product/${p.id}`; return; }
    cart.add(p, null, 1); toast.success("Added to cart"); setAdded(true); setTimeout(() => setAdded(false), 1200);
  };
  return { hasOptions, added, add };
}

function Heart_({ className = "" }) {
  const [on, setOn] = useState(false);
  return (
    <button type="button" aria-label="Wishlist" aria-pressed={on} onClick={(e) => { e.preventDefault(); setOn((v) => !v); }} className={`transition-transform hover:scale-125 ${className}`}>
      <Heart size={15} fill={on ? K.red : "none"} color={on ? K.red : "#a3b0a9"} className={on ? "mk-beat" : ""} />
    </button>
  );
}

/** Vertical product card: category, wishlist heart, photo, option pills, price + discount, name, stars, button. */
export function MarketCard({ p, s, cart, className = "" }) {
  const { slug } = s;
  const { hasOptions, added, add } = useAdd(p, s, cart);
  const soldOut = p.stock <= 0;
  const pills = optionPills(p);
  return (
    <div data-testid={`storefront-product-${p.id}`} className={`mk-card group h-full flex flex-col bg-white rounded-lg border p-3 ${className}`} style={{ borderColor: K.line }}>
      <div className="flex items-center justify-between min-h-[18px]">
        <span className="text-[10px] truncate pr-2" style={{ color: K.muted }}>{p.category}</span>
        <Heart_ />
      </div>
      <Link to={`/store/${slug}/product/${p.id}`} className="relative block mt-1 rounded-md overflow-hidden aspect-[5/4]" style={{ background: "#f6f8f7" }}>
        {p.image ? <ToneImg src={p.image} alt={p.name} loading="lazy" decoding="async" imgClassName="mk-zoom" /> : <Leaf size={34} className="m-auto mt-[28%]" style={{ color: K.muted }} />}
        {soldOut && <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold uppercase tracking-wider" style={{ background: "rgba(255,255,255,.7)", color: K.muted }}>Sold out</span>}
      </Link>
      <div className="min-h-[24px] mt-2.5 flex gap-1.5 flex-wrap">
        {pills.map((v) => <span key={v} className="text-[9px] px-1.5 py-[2px] rounded-full border" style={{ borderColor: K.line, color: K.muted }}>{v}</span>)}
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        <span className="text-[13.5px] font-bold" style={{ color: K.ink }}>{priceText(p)}</span>
        {p.compare_at_price && !hasOptions ? <span className="text-[11px] line-through" style={{ color: K.muted }}>{inr(p.compare_at_price)}</span> : null}
        <Discount p={p} />
      </div>
      <Link to={`/store/${slug}/product/${p.id}`}><h3 className="mt-1 text-[12.5px] leading-snug line-clamp-2 min-h-[2.6em]" style={{ color: K.ink }}>{p.name}</h3></Link>
      <Stars id={p.id} className="mt-1.5" />
      <button type="button" onClick={add} disabled={soldOut} data-testid={`storefront-add-to-cart-btn-${p.id}`} aria-label={hasOptions ? "Select options" : "Add to cart"}
        className="mk-btn mt-3 h-9 w-full rounded-full text-[11.5px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50" style={{ background: K.soft, color: K.primary }}>
        <ShoppingCart size={13} />{soldOut ? "Sold out" : added ? "Added ✓" : hasOptions ? "Select Options" : "Add To Cart"}
      </button>
    </div>
  );
}

/** Compact horizontal card: photo on the left, category, price + discount, name and stars on the right. */
export function MarketRow({ p, s, cart, className = "" }) {
  const { slug } = s;
  const { hasOptions, add } = useAdd(p, s, cart);
  return (
    <div data-testid={`storefront-product-row-${p.id}`} className={`mk-card group relative flex gap-3 bg-white rounded-lg border p-2.5 h-full ${className}`} style={{ borderColor: K.line }}>
      <Link to={`/store/${slug}/product/${p.id}`} className="relative shrink-0 w-[84px] h-[84px] md:w-[92px] md:h-[92px] rounded-md overflow-hidden" style={{ background: "#f6f8f7" }}>
        {p.image && <ToneImg src={p.image} alt={p.name} loading="lazy" imgClassName="mk-zoom" />}
      </Link>
      <div className="min-w-0 flex-1 flex flex-col">
        <div className="flex items-center justify-between"><span className="text-[10px] truncate pr-2" style={{ color: K.muted }}>{p.category}</span><Heart_ /></div>
        <div className="mt-0.5 flex items-center gap-1.5 flex-wrap"><span className="text-[13px] font-bold" style={{ color: K.ink }}>{priceText(p)}</span>{p.compare_at_price && !hasOptions ? <span className="text-[10.5px] line-through" style={{ color: K.muted }}>{inr(p.compare_at_price)}</span> : null}<Discount p={p} /></div>
        <Link to={`/store/${slug}/product/${p.id}`}><h3 className="mt-0.5 text-[12px] leading-snug line-clamp-2" style={{ color: K.ink }}>{p.name}</h3></Link>
        <div className="mt-auto pt-1 flex items-center justify-between">
          <Stars id={p.id} size={10} />
          <button type="button" onClick={add} disabled={p.stock <= 0} aria-label={hasOptions ? "Select options" : "Add to cart"} data-testid={`storefront-add-to-cart-btn-${p.id}`}
            className="mk-btn w-7 h-7 rounded-full inline-flex items-center justify-center disabled:opacity-40" style={{ background: K.soft, color: K.primary }}><ShoppingCart size={13} /></button>
        </div>
      </div>
    </div>
  );
}

export function MarketHeader({ s, cart }) {
  const { store, slug } = s;
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState("");
  const [stuck, setStuck] = useState(false);
  useEffect(() => { const f = () => setStuck(window.scrollY > 40); f(); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f); }, []);
  const search = (e) => { e.preventDefault(); setSearching(false); nav(`/store/${slug}/shop${q ? `?q=${encodeURIComponent(q)}` : ""}`); };
  // Standard theme header: logo, the standard menu, search, cart and the mobile menu.
  return (
    <header className="sticky top-0 z-30 bg-white" style={{ boxShadow: stuck ? "0 6px 22px rgba(20,60,40,.1)" : "none", borderBottom: `1px solid ${K.line}` }}>
      <div className="max-w-[1320px] mx-auto px-5 h-[72px] flex items-center justify-between gap-6">
        <Link to={`/store/${slug}`} data-testid="storefront-name" className="shrink-0 flex items-center gap-2.5 truncate max-w-[300px]">
          <StoreLogo store={store} height={44} fallback={<>
            <span className="mk-spin-slow w-10 h-10 rounded-full flex items-center justify-center text-white" style={{ background: K.primary }}><Leaf size={20} /></span>
            <span className="text-[20px] font-extrabold truncate" style={{ color: K.ink }}>{store.name}</span></>} />
        </Link>
        <nav className="hidden lg:flex items-center gap-8 text-[13px] font-semibold" data-testid="storefront-nav">
          {navLinks(slug).map(([l, to]) => { const on = isNavActive(pathname, slug, to); return (
            <Link key={l} to={to} aria-current={on ? "page" : undefined} className="rx-link py-1 transition-colors hover:opacity-80" style={{ color: on ? K.primary : K.ink }} data-testid={l === "Track Order" ? "storefront-track-link" : undefined}>{l}</Link>
          ); })}
        </nav>
        <div className="flex items-center gap-4" style={{ color: K.ink }}>
          <button onClick={() => setSearching((x) => !x)} aria-label="Search" data-testid="market-search-toggle" className="hover:opacity-60 transition-opacity"><Search size={19} /></button>
          <button onClick={() => cart.setOpen(true)} data-testid="storefront-cart-btn" aria-label="Open cart" className="relative hover:opacity-70 transition-opacity">
            <ShoppingCart size={21} />
            <span key={cart.count} className="mk-pop absolute -top-2 -right-3 min-w-[17px] h-[17px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center" style={{ background: K.accent }}>{cart.count}</span>
          </button>
          <MobileNav slug={slug} color={K.ink} bg="#fff" accent={K.accent} border={K.line} />
        </div>
      </div>
      {searching && (
        <form onSubmit={search} className="border-t rx-fade" style={{ borderColor: K.line, background: K.soft }}>
          <div className="max-w-[1320px] mx-auto px-5 h-14 flex items-center gap-3">
            <Search size={16} style={{ color: K.muted }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for products…" aria-label="Search products" data-testid="market-search" className="flex-1 outline-none text-[14px] bg-transparent" style={{ color: K.ink }} />
            <button type="button" onClick={() => setSearching(false)} aria-label="Close search"><X size={16} /></button>
          </div>
        </form>
      )}
    </header>
  );
}

export function MarketFooter({ s }) {
  const { store, slug } = s;
  const [top, setTop] = useState(false);
  useEffect(() => { const f = () => setTop(window.scrollY > 600); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f); }, []);
  const lines = footerLines(store);
  const icons = { money: Leaf, verified: ShieldCheck, delivery: Truck };
  // Same structure as every theme: trust strip, store details + the three standard link columns, copyright.
  return (
    <footer data-testid="storefront-footer" style={{ background: "#0e3a29", color: "#c6ddd1" }}>
      <div style={{ background: "#12476f00", borderBottom: "1px solid rgba(255,255,255,.1)" }}>
        <div className="max-w-[1320px] mx-auto px-5 py-8 grid sm:grid-cols-3 gap-6">
          {trustItems(store.requires_prescription, store.free_shipping_over).map(([key, h, d], k) => { const I = icons[key]; return (
            <Reveal key={h} delay={k * 100} className="flex gap-4 items-center sm:justify-center">
              <span className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,.08)", color: "#7be0aa" }}><I size={22} /></span>
              <div><div className="text-[15px] font-semibold text-white">{h}</div><p className="text-[12px] mt-0.5" style={{ color: "#9fbcab" }}>{d}</p></div>
            </Reveal>
          ); })}
        </div>
      </div>
      <div className="max-w-[1320px] mx-auto px-5 py-12 grid md:grid-cols-5 gap-9">
        <Reveal className="md:col-span-2">
          <StoreLogo store={store} height={46} fallback={<div className="flex items-center gap-2.5"><span className="w-10 h-10 rounded-full flex items-center justify-center text-white" style={{ background: K.primary }}><Leaf size={20} /></span><span className="text-[22px] font-extrabold text-white">{store.name}</span></div>} />
          <p className="text-[13px] mt-4 flex gap-3"><MapPin size={16} className="mt-0.5 shrink-0" style={{ color: "#7be0aa" }} />{lines.location}</p>
          <p className="text-[13px] mt-2 flex gap-3"><Mail size={16} className="mt-0.5 shrink-0" style={{ color: "#7be0aa" }} />{lines.support}</p>
          <div className="mt-4 flex gap-2.5">{socialItems(s).map((I) => <a key={I.network} href={I.url} target="_blank" rel="noopener noreferrer" aria-label={I.label} title={I.label} data-testid={`footer-social-${I.network}`}
            className="w-9 h-9 rounded-full flex items-center justify-center border transition-all hover:bg-white/10 hover:-translate-y-0.5" style={{ borderColor: "rgba(255,255,255,.25)", color: "#fff" }}><I.icon size={14} /></a>)}</div>
        </Reveal>
        {footerColumns(slug).map(([h, links], i) => (
          <Reveal key={h} delay={(i + 1) * 90}>
            <div className="text-[13px] font-bold uppercase tracking-widest text-white pb-3 border-b" style={{ borderColor: "rgba(255,255,255,.14)" }}>{h}</div>
            <ul className="mt-5 space-y-3 text-[13px]">{links.map(([l, to]) => <li key={l}><Link to={to} className="rx-link hover:text-white">{l}</Link></li>)}</ul>
          </Reveal>
        ))}
      </div>
      <div className="border-t text-center text-[12px] py-5" style={{ borderColor: "rgba(255,255,255,.12)", color: "#9fbcab" }}>© 2026 {store.name} · Powered by <Link to="/" className="underline">Sell24x7</Link></div>
      {top && <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top" className="fixed bottom-6 right-6 z-30 w-11 h-11 rounded-full flex items-center justify-center text-white shadow-lg rx-slide-up hover:-translate-y-1 transition-transform" style={{ background: K.primary }}><ChevronUp size={19} /></button>}
    </footer>
  );
}

